import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { LOCATIONS, locationImageForViewport, START_LOCATION_ID } from "../shared/location.js";
import { StoreController } from "./controller.store.js";
import { appStore } from "./store.js";

const SLIDE_MS = 560;

@customElement("reading-bee-location-backdrop")
export class ReadingBeeLocationBackdrop extends LitElement {
  static override styles = [
    css`
      :host {
        position: absolute;
        inset: 0;
        z-index: 0;
        display: block;
        overflow: hidden;
        pointer-events: none;
      }

      .pane {
        position: absolute;
        inset: 0;
        overflow: hidden;
      }

      .pane img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transform: scale(1.06);
        filter: blur(8px);
      }

      .shade {
        position: absolute;
        inset: 0;
        background: rgba(12, 11, 9, 0.46);
      }

      .pane.prep-right {
        transform: translateX(100%);
      }

      .pane.prep-left {
        transform: translateX(-100%);
      }

      .pane.slide {
        transition: transform ${SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
      }

      .pane.out-left {
        transform: translateX(-100%);
      }

      .pane.out-right {
        transform: translateX(100%);
      }

      @media (prefers-reduced-motion: reduce) {
        .pane.slide {
          transition: none;
        }
      }
    `,
  ];

  @state() private currentId = START_LOCATION_ID;
  @state() private currentSrc = "";
  @state() private leavingSrc: string | null = null;
  @state() private incomingPrep: "left" | "right" | null = null;
  @state() private outgoingDir: "left" | "right" | null = null;
  private slideTimer: ReturnType<typeof setTimeout> | null = null;
  private slideFrame = 0;

  constructor() {
    super();
    new StoreController(this);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.syncIdle();
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
    this.clearSlide();
  }

  override updated(): void {
    const nextId = appStore.currentProfile?.currentLocationId ?? START_LOCATION_ID;
    if (!this.currentSrc) {
      this.syncIdle();
      return;
    }
    if (nextId === this.currentId) {
      if (!this.leavingSrc) {
        const src = this.srcFor(nextId);
        if (src !== this.currentSrc) this.currentSrc = src;
      }
      return;
    }
    this.beginSlide(nextId);
  }

  override render(): TemplateResult {
    return html`
      ${
        this.leavingSrc
          ? html`
              <div class="pane ${this.outgoingDir ? `slide out-${this.outgoingDir}` : ""}">
                <img src=${this.leavingSrc} alt="" />
              </div>
            `
          : ""
      }
      <div class="pane ${this.incomingClass()}">
        <img src=${this.currentSrc} alt="" />
      </div>
      <div class="shade"></div>
    `;
  }

  private incomingClass(): string {
    if (!this.incomingPrep) return "";
    if (this.outgoingDir) return "slide";
    return this.incomingPrep === "right" ? "prep-right" : "prep-left";
  }

  private srcFor(id: string): string {
    return locationImageForViewport(window.innerWidth, window.innerHeight, id);
  }

  private syncIdle = (): void => {
    const id = appStore.currentProfile?.currentLocationId ?? START_LOCATION_ID;
    this.currentId = id;
    this.currentSrc = this.srcFor(id);
    this.leavingSrc = null;
    this.incomingPrep = null;
    this.outgoingDir = null;
  };

  private onResize = (): void => {
    if (this.leavingSrc) return;
    this.currentSrc = this.srcFor(this.currentId);
  };

  private beginSlide(nextId: string): void {
    const nextSrc = this.srcFor(nextId);
    if (this.prefersReducedMotion()) {
      this.currentId = nextId;
      this.currentSrc = nextSrc;
      this.leavingSrc = null;
      this.incomingPrep = null;
      this.outgoingDir = null;
      return;
    }
    const from = Math.max(
      0,
      LOCATIONS.findIndex((item) => item.id === this.currentId),
    );
    const to = Math.max(
      0,
      LOCATIONS.findIndex((item) => item.id === nextId),
    );
    const forward = to >= from;
    this.clearSlide();
    this.leavingSrc = this.currentSrc;
    this.currentId = nextId;
    this.currentSrc = nextSrc;
    this.incomingPrep = forward ? "right" : "left";
    this.outgoingDir = null;
    this.slideFrame = requestAnimationFrame(() => {
      this.slideFrame = requestAnimationFrame(() => {
        this.slideFrame = 0;
        this.outgoingDir = forward ? "left" : "right";
        this.slideTimer = setTimeout(() => {
          this.slideTimer = null;
          this.leavingSrc = null;
          this.incomingPrep = null;
          this.outgoingDir = null;
        }, SLIDE_MS + 40);
      });
    });
  }

  private clearSlide(): void {
    if (this.slideTimer) {
      clearTimeout(this.slideTimer);
      this.slideTimer = null;
    }
    if (this.slideFrame) {
      cancelAnimationFrame(this.slideFrame);
      this.slideFrame = 0;
    }
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-location-backdrop": ReadingBeeLocationBackdrop;
  }
}
